// src/auth/AuthContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api/axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => {
    // Migrate legacy key "token" -> "access_token"
    const legacy = localStorage.getItem('token');
    const current = localStorage.getItem('access_token');
    if (!current && legacy) {
      localStorage.setItem('access_token', legacy);
      localStorage.removeItem('token');
      return legacy;
    }
    return current || null;
  });


  // If a token already exists (page reload), prime axios defaults
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  });

  // Important for deployed environments: route guards must not redirect
  // while we're still hydrating session state.
  const [authReady, setAuthReady] = useState(false);

  // Load current user session from API when token exists
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // No token -> session is "ready" (unauthenticated)
      if (!token) {
        if (!cancelled) {
          setUser(null);
          setAuthReady(true);
        }
        return;
      }

      try {
        const res = await api.get('/api/auth/me');
        if (cancelled) return;

        localStorage.setItem('user', JSON.stringify(res.data));
        setUser(res.data);
      } catch (e) {
        if (cancelled) return;

        // Token invalid/expired -> clear session
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    };

    // When token changes (login/logout), we're temporarily not "ready"
    // until we re-hydrate /me (or clear session).
    setAuthReady(false);
    run();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = (newToken, userInfo) => {
    localStorage.setItem('access_token', newToken);
    setToken(newToken);

    // Ensure axios sends the token immediately (important in deployed environments)
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

    // Optional: set user immediately if provided; /me will refresh it anyway
    if (userInfo) {
      localStorage.setItem('user', JSON.stringify(userInfo));
      setUser(userInfo);
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const value = useMemo(
    () => ({ token, user, authReady, login, logout }),
    [token, user, authReady]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
